/**
 * Die Gegenrichtung in der Oberfläche (HM-040): Konfiguration setzen, Aktionen
 * auslösen, Updates anstoßen — und sehen, was daraus geworden ist.
 *
 * **Bis hierher war diese Seite reine Anzeige**, und der Grund dafür stand in
 * `device-detail.tsx`: „Ein Eingabefeld, das nichts absendet, wäre die
 * unehrlichste Form von Anzeige." Seit es die Endpunkte gibt, gilt der Satz
 * andersherum — eine Liste, die zeigt, was man ändern könnte, und es nicht
 * anbietet, ist derselbe Fehler mit umgekehrtem Vorzeichen.
 *
 * Drei Dinge tragen den Aufbau:
 *
 * **Ein Auftrag ist keine Zusage.** Zwischen dem Klick und dem Ausgeführtsein
 * liegen ein Broker, eine Funkstrecke und ein Gerät, das ablehnen darf. Deshalb
 * hat jede Handlung hier einen Verlauf, und der Zustand darin ist die
 * eigentliche Auskunft: „abgeschickt" ist etwas anderes als „ausgeführt".
 *
 * **Gezeigt wird, was das Gerät kann.** `capabilities.writable` sagt, welche
 * Schlüssel es fernsetzen lässt, `capabilities.commands`, welche Aktionen es
 * annimmt — ohne den Schalter `cloudControl` fehlen dort `reboot` und `ap-on`.
 * Was fehlt, wird **ausgeblendet, nicht ausgegraut** (Regel 10). Ein Schutz ist
 * das nicht; der liegt am Gerät, das mit `denied` quittiert.
 *
 * **Ein Feld, das nicht fernsetzbar ist, bleibt eine Zeile.** Der lokale Broker,
 * das WLAN, `cloudEnabled`: Sie stehen weiter in der Anzeige, nur eben ohne
 * Eingabefeld. Sie wegzulassen hieße zu verschweigen, dass es sie gibt; sie als
 * Feld zu zeigen hieße, eine Änderung zu versprechen, die das Gerät ablehnt.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Checkbox, Input, Select } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { formatTimeAgo } from "../lib/format.js";
import {
  ackErrorLabel,
  COMMAND_KIND_LABELS,
  COMMAND_STATUS_LABELS,
  COMMAND_STATUS_TONES,
  DEVICE_ACTION_LABELS,
  DEVICE_ACTION_VERBS,
  SETTING_LABELS,
} from "../lib/device-labels.js";
/**
 * **Ob ein Schlüssel fernsetzbar ist, steht in `lib/device-report.ts`** — nicht
 * hier. Der Kopf der Detailseite muss dieselbe Frage stellen, um zu wissen, ob
 * er zum Umbenennen hierher verweist; zwei Prüfungen wären zwei Gelegenheiten,
 * verschieden streng zu sein, und genau daraus war ein Widerspruch entstanden.
 */
import { canSetRemotely, settingRows } from "../lib/device-report.js";
/**
 * **Was ein Gerät annimmt, hängt an seiner Klasse** (HM-113): Ein Lesekopf ohne
 * gemeldete Fähigkeiten nimmt alles an, ein Gateway ohne gemeldete Fähigkeiten
 * nichts, ein Gerät hinter dem Gateway nie etwas. Die Unterscheidung steht in
 * `lib/device-profile.ts` und nicht in dieser Datei.
 */
import {
  deviceClass,
  deviceSurface,
  offersCommand,
  remoteWritableKeys,
} from "../lib/device-profile.js";
import { useDeviceCommands } from "../lib/queries.js";
import { DefinitionList } from "./definition-list.js";
import { Section } from "./section.js";
import { useSiteAccess } from "../lib/site-access.js";

type Device = ApiComponents["schemas"]["Device"];
type DeviceCommand = ApiComponents["schemas"]["DeviceCommand"];
type DeviceAction = ApiComponents["schemas"]["SendDeviceCommandRequest"]["command"];
type ReportedSettings = NonNullable<Device["reportedSettings"]>;

/**
 * Die acht fernsetzbaren Schlüssel und wie sie einzugeben sind.
 *
 * **Die Reihenfolge folgt der Frage, nicht dem Protokoll:** erst wie das Gerät
 * heißt, dann was es wie oft sendet, zuletzt wie es sich aktualisiert.
 *
 * Die Grenzen sind die der Firmware (`cts_config_set()`). Sie stehen hier, damit
 * ein unplausibler Wert gar nicht erst abgeschickt wird — **maßgeblich bleibt
 * das Gerät**, das erneut prüft und mit `invalid` ablehnt. Zwei Prüfungen sind
 * zwei Gelegenheiten, unterschiedlich streng zu sein, und die Firmware gewinnt
 * jede davon.
 */
type Field =
  | { readonly key: WritableKey; readonly kind: "text"; readonly hint?: string }
  | {
      readonly key: WritableKey;
      readonly kind: "seconds" | "hours";
      readonly min: number;
      readonly max: number;
    }
  | { readonly key: WritableKey; readonly kind: "boolean"; readonly hint?: string }
  | { readonly key: WritableKey; readonly kind: "channel" };

type WritableKey =
  | "deviceName"
  | "interval"
  | "cloudInterval"
  | "haDiscovery"
  | "otaChannel"
  | "otaAuto"
  | "otaCheckH";

const FIELDS: readonly Field[] = [
  { key: "deviceName", kind: "text", hint: "Leer lassen setzt den Werksnamen zurück." },
  { key: "interval", kind: "seconds", min: 1, max: 3600 },
  { key: "cloudInterval", kind: "seconds", min: 1, max: 43_200 },
  { key: "haDiscovery", kind: "boolean" },
  { key: "otaChannel", kind: "channel" },
  { key: "otaAuto", kind: "boolean" },
  { key: "otaCheckH", kind: "hours", min: 1, max: 168 },
];

/** Was in der Anzeige bleibt: gemeldet, aber nicht fernsetzbar. */
const READ_ONLY_KEYS: readonly (keyof ReportedSettings)[] = [
  "mqttTopic",
  "extModule",
  "cloudEnabled",
  "cloudControl",
];

/* --------------------------------------------------------------------------
 * Konfiguration
 * ----------------------------------------------------------------------- */

export function DeviceConfigSection({
  device,
  onShowCommands,
}: {
  device: Device;
  /**
   * Der Weg zu den Aufträgen.
   *
   * **Seit die Seite Reiter hat, liegt die Quittung woanders.** Vorher stand der
   * Verlauf eine Handbreit unter diesem Formular; jetzt sähe man nach dem
   * Absenden gar nichts — und ein Auftrag, der spurlos verschwindet, ist genau
   * der Eindruck, gegen den der Verlauf gebaut wurde (HM-040).
   */
  onShowCommands: () => void;
}) {
  const settings = device.reportedSettings;
  const writable = remoteWritableKeys(device);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10): Wer nur lesen
  // darf, sieht die gemeldeten Einstellungen als Auskunft, nicht als Formular.
  const readOnly = !useSiteAccess().canWrite;

  /**
   * **Nimmt das Gerät gar nichts entgegen, gibt es kein Formular** — auch kein
   * leeres mit dem Satz, was fehlt: Es fehlt nichts. Was es meldet, steht dann
   * als Auskunft da, samt dem Ort, an dem es sich ändern lässt. So ein Gateway
   * ohne Fernsteuerung (HM-113); an ihm stand bis dahin das Formular des
   * Lesekopfs mit Home-Assistant-Schalter und Update-Kanal.
   */
  if (readOnly || (writable !== null && writable.length === 0)) {
    const rows = settingRows(settings);
    if (rows.length === 0) return null;
    const note = readOnly
      ? "Ändern kann sie, wer diese Anlage bearbeiten darf."
      : "Aus der Ferne setzen lässt sich hier nichts — geändert wird am Gerät selbst.";

    return (
      <Section
        title="Einstellungen am Gerät"
        subtitle={
          device.reportedSettingsAt
            ? `Gemeldet ${formatTimeAgo(device.reportedSettingsAt)}. ${note}`
            : note
        }
      >
        <Card>
          <DefinitionList rows={rows} />
        </Card>
      </Section>
    );
  }

  /**
   * **Ohne gemeldete Konfiguration kein Formular.** Ein leeres Feld
   * abzuschicken hieße, dem Gerät einen Wert zu setzen, dessen bisherigen
   * niemand kennt — und `deviceName: ""` bedeutet „Werksname". Solange das
   * Gerät nichts gemeldet hat, steht hier deshalb der Satz, was fehlt.
   */
  if (settings == null) {
    return (
      <Section title="Konfiguration am Gerät" subtitle="Wie das Gerät eingestellt ist.">
        <Card>
          <p style={NOTE}>
            Das Gerät hat seine Einstellungen noch nicht gemeldet. Es tut das beim Verbinden und
            nach jeder Änderung vor Ort — bis dahin lässt sich von hier nichts setzen, weil niemand
            weiß, was heute eingestellt ist.
          </p>
        </Card>
      </Section>
    );
  }

  return (
    <ConfigForm
      device={device}
      settings={settings}
      writable={writable}
      onShowCommands={onShowCommands}
    />
  );
}

function ConfigForm({
  device,
  settings,
  writable,
  onShowCommands,
}: {
  device: Device;
  settings: ReportedSettings;
  writable: readonly string[] | null;
  onShowCommands: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string | boolean>>(() =>
    initialDraft(settings),
  );
  const [error, setError] = useState<string | null>(null);

  /**
   * Ein neuer Stand vom Gerät überschreibt den Entwurf.
   *
   * Das ist die richtige Richtung: Nach einem angenommenen Auftrag meldet das
   * Gerät `settings` neu, und was dann im Formular stünde, wäre der eigene
   * Entwurf von vorhin — er sähe aus wie der aktuelle Stand und wäre es nicht.
   * Wer gerade tippt, verliert dabei nichts, was er nicht ohnehin gerade
   * abgeschickt hat: Der Änderungszähler springt nur, wenn das Gerät gespeichert
   * hat.
   */
  useEffect(() => {
    setDraft(initialDraft(settings));
  }, [device.reportedSettingsRevision, device.reportedSettingsAt]);

  const send = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { data, error: failure } = await api.POST("/devices/{deviceId}/config", {
        params: { path: { deviceId: device.id } },
        body: {
          settings: patch,
          /**
           * **Der Änderungszähler geht mit.** Stellt jemand vor Ort etwas um,
           * während hier noch die alte Ansicht steht, weist das Gerät den
           * Auftrag mit `stale` ab — statt die Änderung vor Ort stillschweigend
           * zu überschreiben. Ohne ihn gewänne immer die Cloud.
           */
          ...(device.reportedSettingsRevision === null
            ? {}
            : { ifRev: device.reportedSettingsRevision }),
        },
      });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Der Auftrag ging nicht hinaus.");
      }
      return data;
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const changes = collectChanges(draft, settings);
  const problems = FIELDS.flatMap((field) => {
    const problem = validate(field, draft[field.key]);
    return problem === null ? [] : [problem];
  });

  /**
   * **Nur, was das Gerät gemeldet hat** (HM-113). Ein Schlüssel, den es nicht
   * meldet, führt es nicht — ein Feld dafür schickte einen Wert, dessen
   * bisherigen niemand kennt, an ein Gerät, das ihn nicht versteht. Am Lesekopf
   * ändert das nichts, er meldet alle; ein Gateway meldet zwei.
   */
  const editable = FIELDS.filter(
    (field) => canSetRemotely(field.key, writable) && settings[field.key] !== undefined,
  );
  const shown = READ_ONLY_KEYS.filter((key) => settings[key] !== undefined);

  return (
    <>
      <Section
        title="Konfiguration am Gerät"
        subtitle={
          device.reportedSettingsAt
            ? `Stand vom Gerät, gemeldet ${formatTimeAgo(device.reportedSettingsAt)}. Änderungen gehen als Auftrag hinaus und gelten, sobald das Gerät sie bestätigt.`
            : "Änderungen gehen als Auftrag hinaus und gelten, sobald das Gerät sie bestätigt."
        }
      >
        <Card>
          {error !== null && <Alert>{error}</Alert>}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              send.mutate(changes);
            }}
            style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                gap: "var(--sp-4)",
                alignItems: "start",
              }}
            >
              {editable.map((field) => (
                <FieldControl
                  key={field.key}
                  field={field}
                  value={draft[field.key]}
                  problem={validate(field, draft[field.key])}
                  onChange={(value) => setDraft((current) => ({ ...current, [field.key]: value }))}
                />
              ))}
            </div>

            {/* Zählernamen gehören zu den Kanälen und nicht in dieses Raster: Ihre
                Zahl hängt am Gerät, und ein Feld ohne Kanal daneben sagt nicht,
                welchen Lesekopf es benennt. Sie kommen mit HM-007, wenn Zähler an
                den Kanälen hängen. */}

            <div
              style={{
                display: "flex",
                gap: "var(--sp-3)",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                type="submit"
                size="sm"
                disabled={
                  send.isPending || Object.keys(changes).length === 0 || problems.length > 0
                }
              >
                Änderungen an das Gerät senden
              </Button>
              {send.isSuccess ? (
                /* Der Auftrag ist hinaus, das Gerät hat aber noch nicht
                   bestätigt — deshalb steht hier nicht „gesetzt", sondern der
                   Weg zu der Stelle, an der die Antwort ankommt. */
                <>
                  <span style={NOTE}>Der Auftrag ist hinausgegangen.</span>
                  <Button variant="ghost" size="sm" onClick={onShowCommands}>
                    Zu den Aufträgen
                  </Button>
                </>
              ) : (
                <span style={NOTE}>{submitNote(Object.keys(changes).length, problems.length)}</span>
              )}
            </div>
          </form>
        </Card>
      </Section>

      {/**
       * **Ein eigener Abschnitt und keine Liste unter dem Formular.** Was den
       * Zugang des Geräts betrifft, ist nicht „ein Feld, das gerade nicht geht",
       * sondern eine andere Art von Angabe — sie steht deshalb in einer eigenen
       * Karte mit eigener Überschrift, so wie im Entwurf.
       */}
      {shown.length > 0 && (
        <Section
          title="Nur am Gerät änderbar"
          subtitle="Alles, was den Zugang des Geräts betrifft, bleibt vor Ort: an seiner eigenen Oberfläche unter „Konfiguration“."
        >
          <Card>
            <DefinitionList
              rows={shown.map((key) => [SETTING_LABELS[key], displayValue(settings[key])] as const)}
            />
          </Card>
        </Section>
      )}
    </>
  );
}

function FieldControl({
  field,
  value,
  problem,
  onChange,
}: {
  field: Field;
  value: string | boolean | undefined;
  problem: string | null;
  onChange: (value: string | boolean) => void;
}) {
  if (field.kind === "boolean") {
    return (
      <div style={{ paddingTop: 22 }}>
        <Checkbox checked={value === true} label={SETTING_LABELS[field.key]} onChange={onChange} />
      </div>
    );
  }

  if (field.kind === "channel") {
    return (
      <Select
        label={SETTING_LABELS[field.key]}
        size="sm"
        value={typeof value === "string" ? value : "stable"}
        options={[
          { value: "stable", label: "stable — der Regelfall" },
          { value: "dev", label: "dev — Vorabversionen" },
        ]}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <Input
      label={SETTING_LABELS[field.key]}
      size="sm"
      value={typeof value === "string" ? value : ""}
      hint={field.kind === "text" ? field.hint : `${field.min} bis ${field.max}`}
      error={problem ?? undefined}
      inputMode={field.kind === "text" ? undefined : "numeric"}
      maxLength={field.kind === "text" ? 120 : 8}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/* --------------------------------------------------------------------------
 * Aktionen und Updates
 * ----------------------------------------------------------------------- */

/** Die vier Aktionen, die nichts am Betrieb ändern — sie senden etwas. */
const SAFE_ACTIONS: readonly { action: DeviceAction; effect: string }[] = [
  { action: "diag", effect: "Das Gerät schickt WLAN, Laufzeit und den Zustand jedes Lesekopfs." },
  {
    action: "publish",
    effect: "Die aktuellen Werte gehen sofort heraus, ohne auf den Takt zu warten.",
  },
  { action: "hello", effect: "Selbstauskunft, Konfiguration und Diagnose kommen neu." },
];

/** Die beiden Eingriffe. Sie verlangen den Schalter am Gerät. */
const CONTROL_ACTIONS: readonly { action: DeviceAction; effect: string }[] = [
  { action: "reboot", effect: "Das Gerät startet neu. Gepufferte Werte bleiben erhalten." },
  {
    action: "ap-on",
    effect:
      "Der Rückfall-Hotspot geht an — der Weg zum Gerät, wenn das Heimnetz klemmt. Ein Neustart räumt ihn wieder weg.",
  },
];

/**
 * Eine Handlung, wie sie in einer Zeile steht.
 *
 * `heavy` heißt: greift in den Betrieb ein und wird deshalb zweimal geklickt.
 * Der Entwurf schreibt dafür „Auslösen …" — die drei Punkte sagen überall in
 * dieser Oberfläche „es kommt noch eine Rückfrage", und ein Neustart ohne
 * Rückfrage wäre der erste Ort, an dem das nicht stimmte.
 */
type Action = {
  readonly key: string;
  readonly label: string;
  /** Was auf dem Knopf steht — ein Verb, die Zeile nennt die Handlung. */
  readonly verb: string;
  readonly effect: string;
  readonly heavy?: boolean;
  readonly run: () => void;
};

export function DeviceActionsSection({ device }: { device: Device }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const surface = deviceSurface(device);

  const run = useMutation({
    mutationFn: async (action: DeviceAction) => {
      const { data, error: failure } = await api.POST("/devices/{deviceId}/commands", {
        params: { path: { deviceId: device.id } },
        body: { command: action },
      });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Der Auftrag ging nicht hinaus.");
      }
      return data;
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["devices", "commands", device.id] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const ota = useMutation({
    mutationFn: async (action: "check" | "apply") => {
      const { data, error: failure } = await api.POST("/devices/{deviceId}/ota", {
        params: { path: { deviceId: device.id } },
        body: { action },
      });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Der Auftrag ging nicht hinaus.");
      }
      return data;
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["devices", "commands", device.id] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const busy = run.isPending || ota.isPending;

  /**
   * **Gruppiert nach der Frage, nicht nach dem Endpunkt.** Die beiden
   * Update-Aufträge gehen an `/ota` und die übrigen an `/commands` — für den,
   * der die Seite bedient, ist das kein Unterschied. Er sucht nach „etwas
   * abfragen", „Firmware" oder „einmal aus und wieder an".
   */
  const groups: readonly { title: string; subtitle: string; items: readonly Action[] }[] = [
    {
      title: "Abfragen",
      subtitle: "Auskünfte anfordern — nichts davon greift in den Betrieb ein.",
      items: SAFE_ACTIONS.filter(({ action }) => offersCommand(device, action)).map(
        ({ action, effect }) => ({
          key: action,
          label: DEVICE_ACTION_LABELS[action],
          verb: DEVICE_ACTION_VERBS[action],
          effect,
          run: () => run.mutate(action),
        }),
      ),
    },
    {
      title: "Firmware",
      subtitle: "Nachsehen und aufspielen.",
      items: !surface.firmware
        ? []
        : [
            {
              key: "ota-check",
              label: "Nach einem Update suchen",
              verb: "Suchen",
              effect:
                "Das Gerät sieht in seinem Update-Kanal nach und meldet in der Diagnose, ob eines bereitsteht.",
              run: () => ota.mutate("check"),
            },
            {
              key: "ota-apply",
              label: "Update installieren",
              verb: "Installieren",
              effect:
                "Das Gerät lädt und installiert, dann startet es neu. Dass es geklappt hat, erkennt man daran, dass es sich mit neuer Firmware zurückmeldet.",
              heavy: true,
              run: () => ota.mutate("apply"),
            },
          ],
    },
    {
      title: "Neustart und Zugang",
      subtitle: "Eingriffe, die das Gerät kurz aus dem Betrieb nehmen.",
      items: CONTROL_ACTIONS.filter(({ action }) => offersCommand(device, action)).map(
        ({ action, effect }) => ({
          key: action,
          label: DEVICE_ACTION_LABELS[action],
          verb: DEVICE_ACTION_VERBS[action],
          effect,
          heavy: true,
          run: () => run.mutate(action),
        }),
      ),
    },
  ];

  return (
    <>
      {error !== null && <Alert>{error}</Alert>}

      {/* Eine leere Gruppe erscheint nicht — ohne Fernsteuerung gibt es
          „Neustart und Zugang" nicht, und eine Überschrift über einer leeren
          Karte sähe aus wie ein Ladefehler. */}
      {groups
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <Section key={group.title} title={group.title} subtitle={group.subtitle}>
            <Card padding="none">
              {group.items.map((item, index) => (
                <ActionRow
                  key={item.key}
                  action={item}
                  disabled={busy}
                  last={index === group.items.length - 1}
                />
              ))}
            </Card>
          </Section>
        ))}

      {/**
       * **Warum hier zwei Knöpfe fehlen können.** Ohne den Schalter
       * „Fernsteuerung" am Gerät meldet es `reboot` und `ap-on` gar nicht erst
       * als Fähigkeit — und ein Knopf, der zuverlässig `denied` bekommt, ist
       * kein Angebot, sondern eine Falle. Der Satz steht da, damit niemand nach
       * dem fehlenden Knopf sucht.
       */}
      {deviceClass(device) === "own_connection" &&
        device.capabilities != null &&
        !device.capabilities.remoteControl && (
          <p style={NOTE}>
            <strong>Neu starten und Hotspot fehlen</strong>, weil dieses Gerät keine Aufträge
            annimmt, die in seinen Betrieb eingreifen. Der Schalter dafür sitzt an seiner eigenen
            Oberfläche unter „Cloud" und muss vor Ort gesetzt werden.
          </p>
        )}
    </>
  );
}

function ActionRow({
  action,
  disabled,
  last,
}: {
  action: Action;
  disabled: boolean;
  last: boolean;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        gap: "var(--sp-3)",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        padding: "14px var(--sp-4)",
        borderBottom: last ? "none" : "1px solid var(--border-subtle)",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 280px" }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>
          {action.label}
        </span>
        {/* Was danach anders ist — nicht, was der Auftrag heißt. */}
        <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
          {action.effect}
        </span>
      </span>

      <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
        {confirming ? (
          <>
            <Button
              variant="danger"
              size="sm"
              disabled={disabled}
              onClick={() => {
                setConfirming(false);
                action.run();
              }}
            >
              Wirklich {action.verb.toLowerCase()}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Abbrechen
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => (action.heavy === true ? setConfirming(true) : action.run())}
          >
            {action.heavy === true ? `${action.verb} …` : action.verb}
          </Button>
        )}
      </span>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Verlauf
 * ----------------------------------------------------------------------- */

/**
 * Was aus den Aufträgen geworden ist.
 *
 * **Ohne diesen Abschnitt wäre jede Schaltfläche oben eine Behauptung.** Ein
 * Klick, ein Verschwinden, kein Ergebnis: Genau so entsteht der Eindruck, die
 * Fernwartung funktioniere nicht — auch dann, wenn das Gerät den Auftrag
 * ordentlich abgelehnt hat und den Grund mitgeschickt hat.
 */
export function DeviceCommandHistory({
  device,
  hideWhenEmpty = false,
}: {
  device: Device;
  /**
   * An einem Gerät, das Aufträge nur ausnahmsweise bekommt — ein Gerät hinter
   * dem Gateway mit einem schreibbaren Datenpunkt —, ist „noch nichts
   * geschickt" keine Auskunft, sondern eine Karte mehr (HM-113).
   */
  hideWhenEmpty?: boolean;
}) {
  const commands = useDeviceCommands(device.id);
  const items = commands.data?.items ?? [];

  if (commands.isPending) return null;
  if (hideWhenEmpty && items.length === 0) return null;

  return (
    <Section title="Aufträge" subtitle="Was zuletzt an dieses Gerät geschickt wurde.">
      {items.length === 0 ? (
        <Card>
          <p style={NOTE}>
            Bislang wurde nichts an dieses Gerät geschickt. Jeder Auftrag steht danach hier, samt
            dem, was das Gerät geantwortet hat.
          </p>
        </Card>
      ) : (
        <Card padding="none">
          {items.map((command, index) => (
            <CommandRow key={command.id} command={command} last={index === items.length - 1} />
          ))}
        </Card>
      )}
    </Section>
  );
}

function CommandRow({ command, last }: { command: DeviceCommand; last: boolean }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: "var(--sp-3)",
        alignItems: "start",
        padding: "12px var(--sp-4)",
        borderBottom: last ? "none" : "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
          {commandTitle(command)}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
          {formatTimeAgo(command.createdAt)}
          {command.error !== null && ` · ${ackErrorLabel(command.error)}`}
          {/* Der Klartext des Geräts steht nur da, wenn er etwas hinzufügt —
              er ist Anzeige und kein Vertrag. */}
          {command.error === null && command.message !== null && ` · ${command.message}`}
        </div>
      </div>
      <Badge tone={COMMAND_STATUS_TONES[command.status]}>
        {COMMAND_STATUS_LABELS[command.status]}
      </Badge>
    </div>
  );
}

/**
 * Was ein Auftrag war, in einem Satzanfang.
 *
 * Bei einer Konfiguration stehen die gesetzten Schlüssel darin: „Konfiguration:
 * Sendetakt Cloud, Update-Kanal" beantwortet die Frage, was da eigentlich
 * geändert wurde — „Konfiguration" allein lässt sie offen.
 */
function commandTitle(command: DeviceCommand): string {
  if (command.kind === "command" && command.action !== null) {
    return DEVICE_ACTION_LABELS[command.action as DeviceAction] ?? command.action;
  }

  if (command.kind === "ota") {
    return command.action === "apply" ? "Update installieren" : "Nach einem Update suchen";
  }

  /* Ein Wert an einen Datenpunkt (HM-110): Schlüssel und Wert stehen im
     Auftrag — „Wert setzen: charge-mode = 1" sagt, was geschickt wurde. */
  if (command.kind === "dp") {
    const key = command.payload?.["key"];
    const value = command.payload?.["value"];
    return typeof key === "string" && typeof value === "number"
      ? `${COMMAND_KIND_LABELS.dp}: ${key} = ${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 3 }).format(value)}`
      : COMMAND_KIND_LABELS.dp;
  }

  const keys = Object.keys(command.payload ?? {});
  if (keys.length === 0) return COMMAND_KIND_LABELS[command.kind];

  return `Konfiguration: ${keys
    .map((key) => SETTING_LABELS[key as keyof ReportedSettings] ?? key)
    .join(", ")}`;
}

/* --------------------------------------------------------------------------
 * Rechnung
 * ----------------------------------------------------------------------- */

const NOTE = {
  margin: 0,
  fontSize: 12.5,
  lineHeight: 1.6,
  color: "var(--text-muted)",
} as const;

/**
 * Der Satz neben dem Absende-Knopf — **drei Lagen, drei Sätze** (HM-050).
 *
 * „Nichts geändert" und „ein Wert ist unplausibel" standen bis dahin im selben
 * Satz, weil beide den Knopf sperren. Wer sich vertippt hatte, sah damit einen
 * gesperrten Knopf und daneben die Behauptung, es gäbe nichts zu senden —
 * während der Grund rot am Feld stand. Zwei Ursachen mit derselben Wirkung sind
 * noch lange nicht dieselbe Auskunft.
 */
function submitNote(changed: number, problems: number): string {
  if (problems > 0) return "Ein Wert ist unplausibel — er steht rot an seinem Feld.";
  if (changed === 0) return "Keine Änderungen offen.";

  const was =
    changed === 1 ? "Ein Schlüssel wird gesetzt." : `${changed} Schlüssel werden gesetzt.`;
  return `${was} Alles andere bleibt am Gerät unverändert.`;
}

function initialDraft(settings: ReportedSettings): Record<string, string | boolean> {
  const draft: Record<string, string | boolean> = {};
  for (const field of FIELDS) {
    const value = settings[field.key];
    if (field.kind === "boolean") draft[field.key] = value === true;
    else draft[field.key] = typeof value === "string" ? value : "";
  }
  return draft;
}

/**
 * Was sich gegenüber dem gemeldeten Stand geändert hat.
 *
 * **Nur das geht hinaus** — der Auftrag ist ein Merge, und ein unveränderter
 * Schlüssel mitzuschicken hieße, ihn erneut zu setzen. Bei `deviceName` wäre das
 * sogar folgenreich: Ein leerer Wert bedeutet dort „zurück auf den Werksnamen".
 */
function collectChanges(
  draft: Record<string, string | boolean>,
  settings: ReportedSettings,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const field of FIELDS) {
    const next = draft[field.key];
    const current = settings[field.key];

    if (field.kind === "boolean") {
      if (next !== (current === true)) changes[field.key] = next;
      continue;
    }

    const text = typeof next === "string" ? next.trim() : "";
    if (text === (typeof current === "string" ? current : "")) continue;
    // Ein leeres Zahlenfeld ist keine Änderung, sondern ein unfertiger Entwurf.
    if (text === "" && field.kind !== "text") continue;
    changes[field.key] = text;
  }

  return changes;
}

/** Dieselben Grenzen, die die Firmware prüft — hier nur, um Wartezeit zu sparen. */
function validate(field: Field, value: string | boolean | undefined): string | null {
  if (field.kind === "boolean" || field.kind === "channel" || field.kind === "text") return null;

  const text = typeof value === "string" ? value.trim() : "";
  if (text === "") return null;
  if (!/^\d+$/u.test(text)) return "Nur ganze Zahlen.";

  const number = Number(text);
  if (number < field.min || number > field.max) return `${field.min} bis ${field.max}.`;
  return null;
}

function displayValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "ein" : "aus";
  if (Array.isArray(value)) return value.map((entry) => String(entry) || "—").join(", ");
  return String(value);
}
